import { useEffect, useState, useRef } from "react";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { uploadToCloudinary } from "@/lib/cloudinary";
import { Loader2, Upload, ShieldCheck, MapPin, Briefcase, Check, ArrowRight, Star } from "lucide-react";

function StatusBadge({ status }) {
  const map = {
    awaiting_contractor: "bg-blue-500 text-white",
    accepted: "bg-[#8DE038] text-[#050806]",
    completed: "bg-[#1F3325] text-[#8DE038]",
    cancelled: "bg-red-500/30 text-red-300",
  };
  return <span className={`px-2 py-1 text-[10px] font-display uppercase tracking-widest ${map[status] || ""}`}>{status?.replace(/_/g," ")}</span>;
}

export default function ContractorPortal() {
  const { user, refresh } = useAuth();
  const [tab, setTab] = useState("available");
  const [available, setAvailable] = useState([]);
  const [mine, setMine] = useState([]);
  const [loading, setLoading] = useState(false);
  const [docUploading, setDocUploading] = useState(null);
  const [savedToast, setSavedToast] = useState("");
  const [serviceFilter, setServiceFilter] = useState("all");
  const [profile, setProfile] = useState({
    bio: user?.bio || "",
    skills: (user?.skills || []).join(", "),
    service_areas: (user?.service_areas || []).join(", "),
  });
  const insuranceRef = useRef();
  const idRef = useRef();
  const avatarRef = useRef();

  const load = async () => {
    setLoading(true);
    try {
      const [a, m] = await Promise.all([
        user?.contractor_status === "approved" ? api.get("/contractor/available-jobs") : Promise.resolve({ data: [] }),
        api.get("/bookings/mine"),
      ]);
      setAvailable(a.data);
      setMine(m.data);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [user?.id, user?.contractor_status]);

  const accept = async (id) => {
    try { await api.post(`/contractor/jobs/${id}/accept`); await load(); }
    catch (e) { alert(formatErr(e?.response?.data?.detail)); }
  };
  const complete = async (id) => {
    await api.post(`/contractor/jobs/${id}/complete`); await load();
  };

  const uploadDoc = async (file, kind) => {
    setDocUploading(kind);
    try {
      const r = await uploadToCloudinary(file, "docs");
      const update = kind === "insurance" ? { insurance_doc_url: r.secure_url }
                  : kind === "id" ? { id_doc_url: r.secure_url }
                  : { avatar_url: r.secure_url };
      await api.patch("/contractor/profile", update);
      await refresh();
      setSavedToast("Saved ✓"); setTimeout(()=>setSavedToast(""), 2500);
    } catch (e) { alert(formatErr(e?.message)); }
    finally { setDocUploading(null); }
  };

  const saveProfile = async () => {
    try {
      await api.patch("/contractor/profile", {
        bio: profile.bio,
        skills: profile.skills.split(",").map(s=>s.trim()).filter(Boolean),
        service_areas: profile.service_areas.split(",").map(s=>s.trim().toUpperCase()).filter(Boolean),
      });
      await refresh();
      setSavedToast("Profile saved ✓"); setTimeout(()=>setSavedToast(""), 2500);
    } catch (e) { alert(formatErr(e?.response?.data?.detail)); }
  };

  if (!user) return null;

  const isApproved = user.contractor_status === "approved";

  return (
    <div data-testid="contractor-portal" className="pt-32 pb-20">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
          <div>
            <div className="font-display uppercase tracking-[0.3em] text-xs text-[#8DE038] mb-3">/ Contractor Portal</div>
            <h1 className="font-display uppercase tracking-tighter text-4xl md:text-6xl text-[#F0F4F1] heading-accent">Hello, {user.name?.split(" ")[0]}</h1>
          </div>
          <div className="text-right">
            <StatusBadge status={user.contractor_status || "pending"}/>
            {user.rating_count > 0 && (
              <div className="text-[#8DE038] font-display mt-2 flex items-center gap-1 justify-end"><Star size={14} fill="#8DE038"/> {user.rating_avg?.toFixed(1)} <span className="text-[#9CAEA3] text-xs">({user.rating_count})</span></div>
            )}
          </div>
        </div>

        {!isApproved && (
          <div data-testid="not-approved-banner" className="border border-yellow-500/40 bg-yellow-500/10 text-yellow-200 p-4 mb-6">
            <div className="font-display uppercase tracking-widest text-sm">Account under review</div>
            <p className="text-sm mt-1">Upload your ID and proof of insurance below. We'll approve you within 48h and start sending you local jobs.</p>
          </div>
        )}

        {savedToast && <div className="fixed top-24 right-5 bg-[#8DE038] text-[#050806] px-4 py-2 font-display uppercase tracking-widest text-xs z-50">{savedToast}</div>}

        <div className="flex gap-2 mb-6">
          {[
            { id: "available", label: `Available (${available.length})`, t: "tab-available" },
            { id: "mine", label: `My Jobs (${mine.length})`, t: "tab-mine" },
            { id: "profile", label: "Profile & Docs", t: "tab-profile" },
          ].map((t) => (
            <button key={t.id} data-testid={t.t} onClick={()=>setTab(t.id)} className={`px-4 py-2 border font-display uppercase tracking-widest text-xs ${tab === t.id ? "bg-[#8DE038] text-[#050806] border-[#8DE038]" : "border-[#1F3325] text-[#F0F4F1]"}`}>{t.label}</button>
          ))}
        </div>

        {loading && <div className="text-[#9CAEA3] flex items-center gap-2"><Loader2 className="animate-spin" size={16}/> Loading…</div>}

        {tab === "available" && (
          <div className="space-y-3">
            {!isApproved && <div className="text-[#9CAEA3] border border-dashed border-[#1F3325] p-8 text-center">Get approved to see jobs.</div>}
            {isApproved && (
              <div data-testid="service-filter" className="flex flex-wrap gap-2 mb-4">
                {[
                  { id: "all", label: `All (${available.length})` },
                  ...Array.from(new Set(available.map(b => b.service))).map(s => ({ id: s, label: `${s} (${available.filter(b => b.service === s).length})` }))
                ].map(f => (
                  <button key={f.id} data-testid={`filter-${f.id}`} onClick={() => setServiceFilter(f.id)}
                    className={`px-3 py-1.5 border font-display uppercase tracking-widest text-[10px] transition-colors ${
                      serviceFilter === f.id ? "bg-[#8DE038] text-[#050806] border-[#8DE038]" : "border-[#1F3325] text-[#9CAEA3] hover:border-[#8DE038]"
                    }`}>{f.label}</button>
                ))}
              </div>
            )}
            {isApproved && available.length === 0 && <div className="text-[#9CAEA3] border border-dashed border-[#1F3325] p-8 text-center">No available jobs in your area right now. Check back soon.</div>}
            {available.filter(b => serviceFilter === "all" || b.service === serviceFilter).map((b) => (
              <div key={b.id} data-testid={`job-${b.id}`} className="border border-[#1F3325] bg-[#0F1712] p-5 grid md:grid-cols-12 gap-4">
                <div className="md:col-span-3">
                  <div className="font-display uppercase text-xs text-[#8DE038] tracking-widest">{b.preferred_date} · {b.preferred_time}</div>
                  <div className="font-display uppercase text-lg text-[#F0F4F1] mt-1">{b.service}</div>
                  <div className="text-[#9CAEA3] text-sm">£{b.estimated_value_gbp} estimated</div>
                </div>
                <div className="md:col-span-6 text-sm text-[#C8D4CB] space-y-1">
                  <div>📍 {b.address}, {b.postcode}</div>
                  {b.notes && <div className="text-[#9CAEA3] italic">"{b.notes}"</div>}
                  {b.photo_urls?.length > 0 && (
                    <div className="flex gap-1 mt-2">
                      {b.photo_urls.slice(0,4).map((u, i) => (<img key={i} src={u} alt="" className="w-12 h-12 object-cover border border-[#1F3325]"/>))}
                    </div>
                  )}
                </div>
                <div className="md:col-span-3 flex flex-col gap-2">
                  <button onClick={()=>accept(b.id)} data-testid={`accept-${b.id}`} className="btn-primary text-xs justify-center"><Check size={14}/> Accept Job</button>
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "mine" && (
          <div className="space-y-3">
            {mine.length === 0 && <div className="text-[#9CAEA3] border border-dashed border-[#1F3325] p-8 text-center">No jobs yet.</div>}
            {mine.map((b) => (
              <div key={b.id} data-testid={`myjob-${b.id}`} className="border border-[#1F3325] bg-[#0F1712] p-5 grid md:grid-cols-12 gap-4">
                <div className="md:col-span-3">
                  <div className="font-display uppercase text-xs text-[#8DE038] tracking-widest">{b.preferred_date} · {b.preferred_time}</div>
                  <div className="font-display uppercase text-lg text-[#F0F4F1] mt-1">{b.service}</div>
                  <div className="text-[#9CAEA3] text-sm">£{b.estimated_value_gbp} estimated</div>
                </div>
                <div className="md:col-span-5 text-sm text-[#C8D4CB] space-y-1">
                  <div>📍 {b.address}, {b.postcode}</div>
                  <div>📞 {b.customer_phone || "—"} · {b.customer_email}</div>
                  {b.notes && <div className="text-[#9CAEA3] italic">"{b.notes}"</div>}
                </div>
                <div className="md:col-span-2"><StatusBadge status={b.status}/></div>
                <div className="md:col-span-2 flex flex-col gap-2">
                  {b.status === "accepted" && (<button data-testid={`complete-${b.id}`} onClick={()=>complete(b.id)} className="btn-primary text-[10px] justify-center py-2">Mark Complete</button>)}
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "profile" && (
          <div className="space-y-6">
            <div className="border border-[#1F3325] bg-[#0F1712] p-6 md:p-8 grid md:grid-cols-2 gap-6">
              <div>
                <div className="font-display uppercase tracking-widest text-sm text-[#F0F4F1] mb-3 flex items-center gap-2"><ShieldCheck size={16} className="text-[#8DE038]"/> Verification documents</div>
                <div className="space-y-3 text-sm">
                  <div className="border border-[#1F3325] p-3 flex items-center justify-between">
                    <div><div className="text-[#F0F4F1]">Photo ID</div><div className="text-[#9CAEA3] text-xs">{user.id_doc_url ? "Uploaded ✓" : "Not uploaded"}</div></div>
                    <button onClick={()=>idRef.current?.click()} className="btn-secondary text-[10px]">{docUploading === "id" ? <Loader2 className="animate-spin" size={12}/> : <><Upload size={12}/> Upload</>}</button>
                    <input ref={idRef} type="file" accept="image/*,application/pdf" hidden onChange={(e)=>e.target.files[0] && uploadDoc(e.target.files[0], "id")}/>
                  </div>
                  <div className="border border-[#1F3325] p-3 flex items-center justify-between">
                    <div><div className="text-[#F0F4F1]">Insurance</div><div className="text-[#9CAEA3] text-xs">{user.insurance_doc_url ? "Uploaded ✓" : "Not uploaded"}</div></div>
                    <button onClick={()=>insuranceRef.current?.click()} className="btn-secondary text-[10px]">{docUploading === "insurance" ? <Loader2 className="animate-spin" size={12}/> : <><Upload size={12}/> Upload</>}</button>
                    <input ref={insuranceRef} type="file" accept="image/*,application/pdf" hidden onChange={(e)=>e.target.files[0] && uploadDoc(e.target.files[0], "insurance")}/>
                  </div>
                </div>
              </div>
              <div>
                <div className="font-display uppercase tracking-widest text-sm text-[#F0F4F1] mb-3">Profile photo</div>
                <div className="flex items-center gap-4">
                  {user.avatar_url ? <img src={user.avatar_url} alt="" className="w-20 h-20 object-cover border border-[#1F3325]"/> : <div className="w-20 h-20 border border-dashed border-[#1F3325] flex items-center justify-center text-[#6B7C71] text-xs">No photo</div>}
                  <button onClick={()=>avatarRef.current?.click()} className="btn-secondary text-xs">{docUploading === "avatar" ? <Loader2 className="animate-spin" size={12}/> : <><Upload size={12}/> Change</>}</button>
                  <input ref={avatarRef} type="file" accept="image/*" hidden onChange={(e)=>e.target.files[0] && uploadDoc(e.target.files[0], "avatar")}/>
                </div>
              </div>
            </div>

            <div className="border border-[#1F3325] bg-[#0F1712] p-6 md:p-8 space-y-4">
              <div className="font-display uppercase tracking-widest text-sm text-[#F0F4F1] flex items-center gap-2"><Briefcase size={16} className="text-[#8DE038]"/> About your business</div>
              <div>
                <label className="font-display uppercase tracking-widest text-xs text-[#9CAEA3]">Bio</label>
                <textarea data-testid="profile-bio" rows={3} className="gl-input mt-2 resize-none" value={profile.bio} onChange={(e)=>setProfile({...profile, bio: e.target.value})} placeholder="Tell customers about your experience and the work you love…"/>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="font-display uppercase tracking-widest text-xs text-[#9CAEA3]">Skills (comma-separated)</label>
                  <input data-testid="profile-skills" className="gl-input mt-2" value={profile.skills} onChange={(e)=>setProfile({...profile, skills: e.target.value})} placeholder="Lawn care, hedge trimming…"/>
                </div>
                <div>
                  <label className="font-display uppercase tracking-widest text-xs text-[#9CAEA3]">Service areas <span className="text-[#6B7C71]">(postcode outcodes, comma-separated)</span></label>
                  <input data-testid="profile-areas" className="gl-input mt-2" value={profile.service_areas} onChange={(e)=>setProfile({...profile, service_areas: e.target.value})} placeholder="LS1, LS2, LS6"/>
                </div>
              </div>
              <button data-testid="save-profile" onClick={saveProfile} className="btn-primary text-xs">Save profile</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
